<template>
  <div>
    <!-- Navigation Drawer with ChatBox Content -->
    <v-navigation-drawer
      :value="isDrawerOpen"
      @input="$emit('toggle-drawer', $event)"
      fixed
      temporary
      right
      width="40rem"
      class="chatbox-drawer"
    >
      <!-- Toolbar -->
      <v-toolbar color="primary" dark class="drawer-toolbar">
        <v-img
          max-height="40"
          max-width="40"
          class="mr-4"
          src="../assets/Icons/Group_227.svg"
          alt="Chat Icon"
        ></v-img>
        <v-toolbar-title>Ask Me Your Question</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="$emit('toggle-drawer', false)" title="Close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Domain Selector -->
      <div class="pa-4 domain-selector">
        <p class="domain-label">Please select the domain</p>
        <v-row class="pa-0 ma-0">
          <v-col
            v-for="(domainValue, domainKey) in domainOptions"
            :key="domainKey"
            cols="auto"
            class="pa-1"
          >
            <v-chip
              :input-value="selectedDomain === domainKey"
              @click="selectDomain(domainKey)"
              small
              outlined
              :color="selectedDomain === domainKey ? 'primary' : ''"
              :text-color="selectedDomain === domainKey ? 'primary' : ''"
            >
              {{ domainValue }}
            </v-chip>
          </v-col>
        </v-row>
      </div>

      <!-- Messages Container -->
      <div class="messages-container">
        <v-list class="mt-1 mb-1">
          <v-list-item
            v-for="(msg, index) in messages"
            ref="scrollList"
            :key="msg.sequenceNo"
            :index="index"
            :class="{ 'last-item': index === messages.length - 1 }"
            class="message-item"
          >
            <v-list-item-content style="display: block">
              <!-- User Message -->
              <v-row v-if="msg.role == 'USER'" class="mb-1">
                <v-col cols="2" class="pb-0"></v-col>
                <v-col class="pb-0 mb-0">
                  <v-row class="ma-0 pa-1" justify="end">
                    <span style="font-size: 0.7rem">{{
                      formatDateTime(msg.createdOn)
                    }}</span>
                  </v-row>
                  <div class="quesText">
                    {{ msg.content }}
                  </div>
                </v-col>
              </v-row>
              <!-- Agent Message -->
              <v-row v-if="msg.role == 'AGENT'" class="mb-1">
                <v-col class="pt-0 mt-1">
                  <div
                    class="ansText"
                    v-html="renderedMarkdown(msg.content)"
                  ></div>
                </v-col>
              </v-row>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </div>

      <!-- Message Input Area -->
      <div class="message-input-area">
        <v-row class="ma-1">
          <v-col class="pa-1">
            <v-textarea
              v-model="message"
              label="Type your message"
              auto-grow
              rows="2"
              :disabled="
                selectedDomain === null || selectedDomain === undefined
              "
              @keyup.enter="sendQuestion()"
              outlined
              dense
            ></v-textarea>
          </v-col>
          <v-col cols="auto" class="pa-1 d-flex align-center">
            <v-btn
              fab
              x-small
              color="primary"
              :disabled="
                message.length < 1 ||
                disableSendBtn ||
                selectedDomain === null ||
                selectedDomain === undefined
              "
              @click="sendQuestion()"
            >
              <v-icon>mdi-send</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </v-navigation-drawer>
  </div>
</template> 
<script>
import axios from "axios";
import {
  AI_ASSISTANT_ASSIST,
  AI_ASSISTANT_GET,
  BASE_URL,
} from "@/common/apiEndpoints.js";
import { GET_TOKEN, GET_USER_TYPE } from "@/store/getters";
import {
  USER_TYPE_STUDENT,
  DOMAINS,
  USER_TYPE_TEACHER,
} from "@/common/constants";
import { mapGetters, mapMutations } from "vuex";
import { formatDateTime, getAxiosOptions } from "@/common/utility";
import { SET_OVERLAY_VALUE } from "@/store/mutations";
import MarkdownIt from "markdown-it";
const md = new MarkdownIt({
  html: true, // allow HTML tags
  linkify: true, // auto-detect links
  typographer: true, // smart quotes, etc.
});
md.enable("table");
export default {
  props: {
    isDrawerOpen: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      USER_TYPE_STUDENT: USER_TYPE_STUDENT,
      domainOptions: DOMAINS,
      selectedDomain: "GENERAL",
      previousMessages: [],
      message: "",
      disableSendBtn: false,
      messages: [],
      currentConversationId: null,
      currentMessage: [
        {
          role: "USER",
          content: "",
          createdOn: "",
        },
        {
          role: "AGENT",
          content: "",
          createdOn: "",
        },
      ],
    };
  },

  computed: {
    ...mapGetters({
      userType: `schoolModule/${GET_USER_TYPE}`,
      token: `schoolModule/${GET_TOKEN}`,
    }),
  },
  watch: {
    messages: {
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      },
      deep: true,
    },
  },
  methods: {
    ...mapMutations({
      setOverlayValue: `schoolModule/${SET_OVERLAY_VALUE}`,
    }),
    formatDateTime(date) {
      return formatDateTime(date);
    },
    getAllPreviousMessages() {
      let url = AI_ASSISTANT_GET;
      axios.get(url, getAxiosOptions(BASE_URL, this.token)).then((res) => {
        this.previousMessages = res.data.list;
        this.disableSendBtn = false;
      });
    },
    renderedMarkdown(messageContent) {
      return md.render(messageContent);
    },
    selectDomain(domainKey) {
      this.selectedDomain = domainKey;
      this.currentConversationId = null;
    },
    sendQuestion() {
      if (
        this.message &&
        this.selectedDomain !== null &&
        this.selectedDomain !== undefined
      ) {
        const payload = {};
        payload.message = this.message;
        payload.domain = this.selectedDomain;
        if (this.messages.length > 0 && this.currentConversationId) {
          payload.convId = this.currentConversationId;
        }
        let url = AI_ASSISTANT_ASSIST;
        this.showTypingIndicator();
        this.message = "";
        this.disableSendBtn = true;
        axios
          .post(url, payload, getAxiosOptions(BASE_URL, this.token))
          .then((res) => {
            this.currentConversationId = res.data.convId;
            this.messages.splice(this.messages.length - 2, 2);
            this.messages.push(...res.data.messages);
            this.disableSendBtn = false;
          })
          .catch((error) => {
            this.handleError(error);
            this.disableSendBtn = false;
            error = error.errorMessage;
          });
      }
    },
    showTypingIndicator() {
      this.currentMessage[0].createdOn = new Date();
      this.currentMessage[0].content = this.message;
      this.currentMessage[1].createdOn = new Date();
      this.currentMessage[1].content = "Typing...";
      this.messages.push(...this.currentMessage);
    },
    handleError(error) {
      this.messages.splice(this.messages.length - 1, 1);
      this.messages.push({
        role: "AGENT",
        content:
          error.errorMessage ||
          "Sorry, something went wrong. Please try again later.",
        createdOn: new Date(),
      });
    },
    scrollToBottom() {
      const messagesContainer = document.querySelector(".messages-container");
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    },
  },
};
</script>
<style scoped>
/* Drawer Styles */
.chatbox-drawer {
  display: flex;
  flex-direction: column;
  overflow: hidden !important;
}

/* Hide scrollbar on drawer, show only on messages container */
.chatbox-drawer ::-webkit-scrollbar {
  display: none;
}

.chatbox-drawer {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.drawer-toolbar {
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 2;
}

/* Domain Selector */
.domain-selector {
  flex-shrink: 0;
  border-bottom: 1px solid #e0e0e0;
}

.domain-label {
  font-size: 0.75rem;
  font-weight: 400;
  margin: 0 0 0.5rem 0;
}

/* Messages Container */
.messages-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 65%;
  max-height: 65%;
  padding: 0.5rem 0;
}

/* Message Input Area - Sticks to Bottom */
.message-input-area {
  border-top: 1px solid #e0e0e0;
  background-color: #fff;
}

/* Message Item Styles */
.message-item {
  align-items: flex-start;
}

/* User Message Box */
.quesText {
  border-radius: 0.5rem;
  border: 0.05rem solid;
  padding: 0.5rem;
  font-size: 0.8rem;
  line-height: 1.4;
  word-wrap: break-word;
  overflow-wrap: break-word;
  background-color: #f3efef;
}

/* Agent Message Markdown Content Box */
.ansText {
  padding: 0.5rem;
  font-size: 0.8rem;
  line-height: 1.4;
  width: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

/* List Item Content */
.v-list-item__content {
  margin-bottom: 0.25rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}

/* Message Textarea - Reduced Height */
.message-textarea.v-text-field--outlined > .v-input__control > .v-input__slot {
  padding: 4px 8px !important;
}

.message-textarea.v-textarea textarea {
  line-height: 1rem;
  min-height: 0 !important;
}
</style>

<style>
/* Markdown Table Styles (Global/Unscoped for v-html) */
.ansText table {
  border-collapse: collapse;
  width: 100%;
  margin: 1rem 0;
  border: 1px solid #ddd;
}

.ansText th,
.ansText td {
  border: 1px solid #ddd !important;
  padding: 8px;
  text-align: left;
}

.ansText th {
  background-color: #f5f5f5;
  font-weight: 600;
}

.ansText tr:nth-child(even) {
  background-color: #fafafa;
}
</style>
